import React from 'react';
import {
    getChatApiVersionSnapshot,
    subscribeChatApiVersion,
    writeChatApiVersion,
    type ChatApiVersion
} from '../services/chatApiVersion';

export interface UseChatApiVersionResult {
    version: ChatApiVersion;
    /** Switches, persists, and runs the caller's cleanup. */
    setVersion: (next: ChatApiVersion) => void;
}

/**
 * Holds the chat API version for a surface, and makes switching safe.
 *
 * The value itself is trivial. The reason this is a hook is `onSwitch`: a
 * version change invalidates everything the previous version cached, and
 * forgetting that is what turns a rollback into a second incident.
 *
 * A v1 message id is a Monday update id; a v2 message id is a UUID. Cached
 * messages from one version, edited or deleted through the other, address rows
 * that do not exist. The dashboard also persists its query cache to
 * localStorage, so a stale feed outlives a reload and the switch looks broken
 * rather than reverted.
 *
 * Callers pass what they own — query invalidation, a provider remount — and use
 * the returned `version` as a React `key` so the chat tree is rebuilt rather
 * than reconciled onto data of the other shape.
 */
export function useChatApiVersion(onSwitch?: () => void): UseChatApiVersionResult {
    // Shared store rather than local state: the toggle lives inside the tree
    // whose root picks the service, so a purely local value would update the
    // button and nothing else.
    const version = React.useSyncExternalStore(
        subscribeChatApiVersion,
        getChatApiVersionSnapshot,
        getChatApiVersionSnapshot
    );

    const setVersion = React.useCallback(
        (next: ChatApiVersion): void => {
            if (next === version) {
                return;
            }
            writeChatApiVersion(next);
            onSwitch?.();
        },
        [version, onSwitch]
    );

    return { version, setVersion };
}
